import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import { RenderIf } from 'react-rainbow-components';
import { hideAppSpinner, showAppMessage, showAppSpinner } from '@rainbow-modules/app';
import { Customer } from 'data/firestore/agent/customer/types';
import {
    InvoiceBuilderTemplate,
    InvoiceCreateLine,
    InvoiceCreateStatus,
} from '../types';
import { useLastInvoiceCreateStatus } from './StatusPicker';
import {
    customColumns,
    dollarsToMinor,
    fromIsoDate,
    addDaysIso,
    storedCustomValue,
    todayIsoDate,
    toIsoDate,
    billToCustomValuesPayload,
    orderedColumns,
    newIdempotencyKey,
} from '../helpers';
import { InvoiceBuilderApiError, useCreateInvoice, useInvoiceTemplates } from '../api/hooks';
import {
    INVOICE_CUSTOMER_INVALID,
    INVOICE_TEMPLATE_VERSION_CONFLICT,
} from '../constants';
import Fields, { emptyLine, LineDraft } from './fields';
import FormFooter, { DrawerMode } from './footer';
import Preview from './Preview';
import {
    Container,
    Content,
    FormContainer,
    PreviewPane,
    StyledDrawer,
    StyledUniversalForm,
} from './styled';

const FORM_ID = 'create-invoice-form';

interface CreateInvoiceDrawerProps {
    isOpen?: boolean;
    organizationId: string;
    lockedCustomerId?: string;
    onRequestClose: () => void;
    onCreated: () => void;
}

const CreateInvoiceDrawer = ({
    isOpen,
    organizationId,
    lockedCustomerId,
    onRequestClose,
    onCreated,
}: CreateInvoiceDrawerProps) => {
    const [mode, setMode] = useState<DrawerMode>('form');
    const { getLastStatus } = useLastInvoiceCreateStatus();
    const { mutateAsync: createInvoice } = useCreateInvoice(organizationId);
    const { refetch: refetchTemplates } = useInvoiceTemplates(organizationId, {
        enabled: Boolean(isOpen && organizationId),
    });

    useEffect(() => {
        if (!isOpen) setMode('form');
    }, [isOpen]);

    const initialValuesRef = useRef<Record<string, unknown>>();
    if (!isOpen) {
        initialValuesRef.current = undefined;
    } else if (!initialValuesRef.current) {
        initialValuesRef.current = {
            issuedOn: fromIsoDate(todayIsoDate()),
            dueOn: fromIsoDate(addDaysIso(todayIsoDate(), 30)),
            status: getLastStatus(),
            lines: [emptyLine()],
            billToCustomValues: {},
            notes: '',
            internalNote: '',
        };
    }
    const initialValues = initialValuesRef.current;

    const handleSubmit = useCallback(async (values: Record<string, unknown>) => {
        const template = values.template as InvoiceBuilderTemplate | undefined;
        const customer = values.customer as Customer | undefined;
        const lines = (values.lines as LineDraft[]) || [];
        const billToValues = (values.billToCustomValues as Record<string, unknown>) || {};
        if (!template) return;

        const columns = orderedColumns(template.design.columns || []);
        const notes = String(values.notes || '').trim();
        const internalNote = String(values.internalNote || '').trim();
        const payloadLines: InvoiceCreateLine[] = lines.map((line) => ({
            description: line.description,
            quantity: line.quantity,
            rateMinor: dollarsToMinor(line.rate),
            customValues: customColumns(columns).map((column) => ({
                columnId: column.id,
                value: storedCustomValue(column, line.customValues?.[column.id]),
            })),
        }));

        showAppSpinner();
        try {
            await createInvoice({
                idempotencyKey: newIdempotencyKey(),
                templateId: template.id,
                templateVersionId: template.versionId,
                customerId: lockedCustomerId || customer?.id || '',
                issuedOn: toIsoDate(values.issuedOn as Date),
                dueOn: toIsoDate(values.dueOn as Date),
                status: values.status as InvoiceCreateStatus,
                lines: payloadLines,
                billToCustomValues: billToCustomValuesPayload(
                    template.design.billToFields,
                    billToValues,
                ),
                ...(notes ? { note: notes } : {}),
                ...(internalNote ? { internalNote } : {}),
            });
            onCreated();
            onRequestClose();
        } catch (error) {
            const isApiError = error instanceof InvoiceBuilderApiError;
            if (
                isApiError
                && error.code === INVOICE_TEMPLATE_VERSION_CONFLICT
            ) {
                await refetchTemplates();
            }
            showAppMessage({
                variant: 'error',
                message: isApiError && error.code === INVOICE_CUSTOMER_INVALID
                    ? 'The selected customer has no name, phone number, or email. Add at least one before creating the invoice.'
                    : (error as Error).message,
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [createInvoice, lockedCustomerId, onCreated, onRequestClose, refetchTemplates]);

    const handleRequestClose = useCallback(() => {
        if (mode === 'preview') return;
        onRequestClose();
    }, [mode, onRequestClose]);

    return (
        <StyledDrawer
            isOpen={isOpen}
            header={mode === 'preview' ? 'Invoice preview' : 'Create invoice'}
            slideFrom="right"
            hideCloseButton={mode === 'preview'}
            onRequestClose={handleRequestClose}
        >
            <RenderIf isTrue={isOpen}>
                <Container>
                    <StyledUniversalForm
                        id={FORM_ID}
                        onSubmit={handleSubmit}
                        initialValues={initialValues}
                        keepDirtyOnReinitialize
                    >
                        <Content $hidden={mode === 'preview'}>
                            <FormContainer>
                                <Fields
                                    organizationId={organizationId}
                                    lockedCustomerId={lockedCustomerId}
                                />
                            </FormContainer>
                        </Content>
                        <RenderIf isTrue={mode === 'preview'}>
                            <PreviewPane>
                                <Preview
                                    organizationId={organizationId}
                                    lockedCustomerId={lockedCustomerId}
                                />
                            </PreviewPane>
                        </RenderIf>
                        <FormFooter
                            formId={FORM_ID}
                            mode={mode}
                            onPreviewClick={() => setMode('preview')}
                            onClosePreviewClick={() => setMode('form')}
                            onCancelClick={onRequestClose}
                        />
                    </StyledUniversalForm>
                </Container>
            </RenderIf>
        </StyledDrawer>
    );
};

export default CreateInvoiceDrawer;
